package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.TypeAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.type.IrType

/**
 * M5-c #I5：state defaults 显式类型标注与字面量默认值的一致性校验。
 *
 * <p>仅在 DSL 作者显式书写类型时才启用：
 * <ul>
 *   <li>未写类型 → 视为 AnyT，不报错，保持旧资源兼容</li>
 *   <li>写了类型但默认值字面量不相容 → 报 DSL_E620</li>
 * </ul>
 * </p>
 */
class StateTypeChecker(private val diag: DiagnosticCollector) {

    fun check(screen: ScreenAst) {
        screen.state?.entries?.forEach { entry ->
            val declared = entry.type ?: return@forEach
            val declaredIr = toIrType(declared)
            val actualIr = literalType(entry.literal)
            if (!isLiteralAssignableTo(actualIr, declaredIr)) {
                diag.error(
                    DslError.E620_TYPE_MISMATCH,
                    entry.literal.position,
                    declaredIr.displayName(),
                    actualIr.displayName(),
                )
            }
        }
    }

    /**
     * 与 [IrType.isAssignableTo] 一致，但额外特判 `null` 字面量赋给可空目标的场景。
     *
     * 字面量 null 的推断类型为 `Nullable(AnyT)`，对任何 `Nullable(X)` 目标都应放行；
     * 但 [IrType.isAssignableTo] 在 this 为 Nullable 时一律拒绝赋给非空目标，对 Nullable
     * 目标也不做"AnyT inner"穿透判断，故此处复用 [TypeConstraintChecker] 中的同一特判。
     */
    private fun isLiteralAssignableTo(actual: IrType, declared: IrType): Boolean {
        if (actual is IrType.Nullable && actual.inner == IrType.AnyT && declared is IrType.Nullable) {
            return true
        }
        return actual.isAssignableTo(declared)
    }

    /**
     * 推断 state 默认值字面量的编译期类型。
     *
     * - 基础字面量：String / Number / Boolean 一一对应；
     * - `null` → `Nullable(AnyT)`：仅可赋给可空目标，赋给非空目标触发 E620；
     * - 列表 / Map 字面量：归类为 ListT / MapT，不下钻元素类型，避免与 RFC 商店 demo 实际
     *   存在的"List 内含 Map 字面量做 data 实例化"形态产生假阳性；
     * - 其它（理论上 parser 会拦下）：退化为 AnyT。
     */
    private fun literalType(lit: ExprAst): IrType = when (lit) {
        is ExprAst.Literal.StringLit -> IrType.StringT
        is ExprAst.Literal.NumberLit -> IrType.NumberT
        is ExprAst.Literal.BoolLit -> IrType.BooleanT
        is ExprAst.NullLit -> IrType.Nullable(IrType.AnyT)
        is ExprAst.ListLiteralExpr -> IrType.ListT()
        is ExprAst.MapLiteralExpr -> IrType.MapT()
        else -> IrType.AnyT
    }

    /**
     * 把 state 字段类型标注映射到编译期类型系统，供 `literal -> declared` 可赋值判断使用。
     *
     * <p>与 StateEmitter 的 wire 映射保持同一语义：
     * 基础内置类型精确映射，未知/暂不支持细分的类型退化为 AnyT，避免编译期假阳性。</p>
     */
    private fun toIrType(t: TypeAst): IrType = when (t) {
        is TypeAst.Named -> {
            val base = when (t.name) {
                "Int", "Long", "Double", "Number" -> IrType.NumberT
                "String" -> IrType.StringT
                "Bool", "Boolean" -> IrType.BooleanT
                "Any" -> IrType.AnyT
                "Unit" -> IrType.UnitT
                else -> IrType.AnyT
            }
            if (t.nullable) IrType.Nullable(base) else base
        }
        is TypeAst.Generic -> {
            val base = when (t.name) {
                "List" -> IrType.ListT()
                "Map" -> IrType.MapT()
                else -> IrType.AnyT
            }
            if (t.nullable) IrType.Nullable(base) else base
        }
        is TypeAst.Function -> {
            val base = IrType.AnyT
            if (t.nullable) IrType.Nullable(base) else base
        }
    }
}
